import { MASK } from '../../../../constants/mask';
import SelectDc from '../../../../components/Selects/SelectDc';
import { isValidIban, normalizeIban } from '../../../../constants/kzBanks';

export const REQUISITES_FIELDS = {
  dcName: 'name',
  bin: 'bin',
  address: 'address',
  iban: 'iban',
  bankName: 'bankName',
  bic: 'bic',
};

// Заголовок блока реквизитов: если бэк подставил посредника, менеджер должен видеть, чьи это реквизиты
// (в договоре Агентом будет он, при сохранении сервер подставит их сам).
export const requisitesTitle = (viaIntermediary) =>
  viaIntermediary
    ? '4. Реквизиты посредника (подставлены автоматически, уйдут в договор)'
    : '4. Реквизиты дилерского центра';

// Контрольную сумму показываем предупреждением, а не ошибкой: счёт бывает нужен
// и такой, какой дал клиент, а на тесте в поля вводят заведомо выдуманные номера.
const ibanWarning = (values) => {
  const iban = normalizeIban(values?.iban);
  if (iban.length < 20) return undefined;
  return isValidIban(iban) ? undefined : 'Проверьте номер счёта: контрольная сумма не сходится';
};

export const REQUISITES_INPUTS = [
  {
    name: 'dcName',
    label: 'Наименование дилерского центра',
    placeholder: 'ТОО «...»',
    rules: { required: true },
  },
  {
    name: 'bin',
    label: 'БИН',
    placeholder: '000000000000',
    mask: MASK.IIN,
    rules: { required: true },
  },
  {
    name: 'address',
    label: 'Юридический адрес',
    placeholder: 'г. Алматы, ул. ...',
    rules: { required: true },
  },
  {
    name: 'iban',
    label: 'Номер банковского счёта (IBAN)',
    placeholder: 'KZ00000000000000000',
    mask: MASK.IBAN,
    warning: ibanWarning,
    rules: {
      required: true,
      validate: (value) => {
        const clean = (value || '').replace(/_/g, '');
        return clean.length === 20 || 'Номер счёта должен содержать 20 символов';
      },
    },
  },
  {
    name: 'bankName',
    label: 'Наименование банка',
    placeholder: 'АО «Банк»',
    rules: { required: true },
  },
  {
    name: 'bic',
    label: 'БИК',
    placeholder: 'KCJBKZKX',
    maxLength: 8,
    transform: (value) => value?.toUpperCase(),
    rules: {
      required: true,
      validate: (value) => (value || '').replace(/_/g, '').length === 8 || 'БИК должен содержать 8 символов',
    },
  },
];

export const SELLER_REQUISITES_INPUTS = [
  {
    name: 'recipient',
    label: 'Получатель',
    placeholder: 'ФИО, как в банке',
    rules: { required: true },
  },
  {
    name: 'iban',
    label: 'IBAN продавца',
    placeholder: 'KZ000000000000000000',
    mask: MASK.IBAN,
    warning: ibanWarning,
    rules: {
      required: true,
      validate: (value) => {
        const clean = (value || '').replace(/[_ ]/g, '').toUpperCase();
        return /^KZ[0-9A-Z]{18}$/.test(clean) || 'Казахстанский IBAN — KZ и 18 символов';
      },
    },
  },
  {
    name: 'bankName',
    label: 'Банк продавца',
    placeholder: 'АО «Банк»',
    rules: { required: true },
  },
  {
    name: 'bic',
    label: 'БИК / BIC продавца',
    placeholder: 'KCJBKZKX',
    maxLength: 11,
    transform: (value) => value?.toUpperCase(),
    rules: {
      required: true,
      validate: (value) => {
        const clean = (value || '').replace(/[_ ]/g, '').toUpperCase();
        return /^[A-Z]{8}([A-Z0-9]{3})?$/.test(clean) || 'БИК — 8 латинских или SWIFT на 11';
      },
    },
  },
];

/**
 * Продавец серого дилера — сам ДЦ: один селект вместо клиента CRM. Выбор подставляет счёт ДЦ
 * (см. sellerAccountOf), а агентом договора остаётся посредник контура — его бэк подставит сам.
 */
export const buildSellerDcInput = ({ onChange, used }) => ({
  name: 'dc',
  component: SelectDc,
  label: 'Продавец — дилерский центр',
  placeholder: 'Выберите из списка',
  isMulti: false,
  used: used || undefined,
  rules: { required: true },
  onChangeCustom: onChange,
});

/** Реквизиты Агента из ответа getDcRequisites — в той форме, что уходит в createConsignment. */
export const agentRequisitesOf = (res) =>
  Object.fromEntries(Object.entries(REQUISITES_FIELDS).map(([field, key]) => [field, res?.[key] ?? '']));

/** Счёт продавца-ДЦ из его собственных реквизитов (res.own в getDcRequisites); без них — пусто. */
export const sellerAccountOf = (own) => ({
  recipient: own?.name ?? '',
  iban: own?.iban ?? '',
  bankName: own?.bankName ?? '',
  bic: own?.bic ?? '',
});

/**
 * Собственный ДЦ серого дилера для автовыбора: единственный в списке, не считая посредника (Есиль).
 * Несколько — выбирать менеджеру; ни одного — тоже: селект остаётся на экране.
 */
export const autoSellerDc = (list) => {
  const own = (Array.isArray(list) ? list : []).filter((dc) => !dc?.isIntermediary);
  return own.length === 1 ? { value: own[0].id, label: own[0].name, cityId: own[0].cityId } : null;
};

/** Реквизиты Агента заполнены целиком — тогда блок «Реквизиты ДЦ» менеджеру не нужен. */
export const agentRequisitesComplete = (requisites) =>
  Object.keys(REQUISITES_FIELDS).every((field) => String(requisites?.[field] ?? '').trim());

/** Счёт продавца-ДЦ заполнен целиком — менеджеру его показывать незачем. */
export const sellerAccountComplete = (own) => Object.values(sellerAccountOf(own)).every((value) => String(value).trim());

/** Поля счёта убираем с экрана, но оставляем в форме: значения из карточки ДЦ уходят в сохранение. */
export const hideInputs = (inputs, hidden) =>
  hidden ? inputs.map((input) => ({ ...input, style: { ...input.style, display: 'none' } })) : inputs;

export const sellerFio = (seller) => {
  const client = seller?.client || seller;
  if (!client) return seller?.label || '';
  return [client.surname, client.name, client.middlename].filter(Boolean).join(' ').trim();
};

export const withDefaults = (inputs, values) =>
  inputs.map((item) => ({ ...item, defaultValue: values?.[item.name] ?? '' }));

export const cleanBank = (value) =>
  String(value || '')
    .replace(/[_ ]/g, '')
    .toUpperCase();
